import { memo, type ReactElement, useCallback, useMemo, useState } from "react";

import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { useSidebar } from "@/components/layout/AppShell";
import { ROUTES } from "@/constants";
import { cn } from "@/lib/utils/cn";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { logout } from "@/store/slices/userSlice";
import { useAuth } from "@/features/auth/useAuth";
import { useEventsFeature } from "@/features/events/useEventsFeature";
import { removeSessionCookies } from "@/lib/auth/session";
import { apolloClient } from "@/lib/apollo/client";
import { isClubleaDomain } from "@/lib/utils/helper";
import synkd_logo from "@/assets/images/synkd_logo.png";
import clublea_logo from "@/assets/images/clublea.svg";

// ─── SVG icon set (inline — no external dep needed for layout) ────
const Icons = {
  home: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path d="M10.707 2.293a1 1 0 0 0-1.414 0l-7 7a1 1 0 0 0 1.414 1.414L4 10.414V17a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-3h2v3a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-6.586l.293.293a1 1 0 0 0 1.414-1.414l-7-7Z" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path
        fillRule="evenodd"
        d="M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0 1.224 1.224 0 0 1-.569 1.175A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z" />
    </svg>
  ),
  shop: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path d="M1 1.75A.75.75 0 0 1 1.75 1h1.628a1.75 1.75 0 0 1 1.734 1.51L5.18 3a65.25 65.25 0 0 1 13.36 1.412.75.75 0 0 1 .58.875 48.645 48.645 0 0 1-1.618 6.2.75.75 0 0 1-.712.513H6a2.503 2.503 0 0 0-2.292 1.5H17.25a.75.75 0 0 1 0 1.5H2.76a.75.75 0 0 1-.748-.807 4.002 4.002 0 0 1 2.716-3.486L3.626 2.716a.25.25 0 0 0-.248-.216H1.75A.75.75 0 0 1 1 1.75ZM6 17.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM15.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
    </svg>
  ),
  suppliers: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path d="M6 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM18 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM10 12a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" />
    </svg>
  ),
  announcements: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path d="M13.92 3.845a19.361 19.361 0 0 1-6.3 1.98C6.765 5.942 5.89 6 5 6a4 4 0 0 0-.504 7.969l.5 3.531H7v-3.031c.7.031 1.407.032 2.1 0L11 18h2l-.504-3.531A4.002 4.002 0 0 0 9 6.5v-.025a19.15 19.15 0 0 0 4.92-2.63Z" />
    </svg>
  ),
  policies: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path
        fillRule="evenodd"
        d="M4 4a2 2 0 0 1 2-2h4.586A2 2 0 0 1 12 2.586L15.414 6A2 2 0 0 1 16 7.414V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4Zm2 6a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1Zm1 3a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2H7Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  team: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path d="M10 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM1.49 15.326a.78.78 0 0 1-.358-.442 3 3 0 0 1 4.308-3.516 6.484 6.484 0 0 0-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 0 1-2.07-.655ZM16.44 15.98a4.97 4.97 0 0 0 2.07-.654.78.78 0 0 0 .357-.442 3 3 0 0 0-4.308-3.517 6.484 6.484 0 0 1 1.907 3.96 2.32 2.32 0 0 1-.026.654ZM18 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM5.304 16.19a.844.844 0 0 1-.277-.71 5 5 0 0 1 9.947 0 .843.843 0 0 1-.277.71A6.975 6.975 0 0 1 10 18a6.974 6.974 0 0 1-4.696-1.81Z" />
    </svg>
  ),
  fixtures: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  orders: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path
        fillRule="evenodd"
        d="M.99 5.24A2.25 2.25 0 0 1 3.25 3h13.5A2.25 2.25 0 0 1 19 5.25l.01 9.5A2.25 2.25 0 0 1 16.76 17H3.26A2.272 2.272 0 0 1 1 14.75l-.01-9.51Zm8.26 9.52v-.625a.75.75 0 0 0-1.5 0v.625a.75.75 0 0 0 1.5 0Zm-4-7.5a.75.75 0 0 0 0 1.5h10.5a.75.75 0 0 0 0-1.5H5.25Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  management: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path
        fillRule="evenodd"
        d="M14.5 10a4.5 4.5 0 0 0 4.284-5.882c-.105-.324-.51-.391-.752-.15L15.34 6.66a.454.454 0 0 1-.493.11 3.01 3.01 0 0 1-1.618-1.616.455.455 0 0 1 .11-.494l2.694-2.692c.24-.241.174-.647-.15-.752a4.5 4.5 0 0 0-5.873 4.575c.055.873-.128 1.808-.8 2.368l-7.23 6.024a2.724 2.724 0 1 0 3.837 3.837l6.024-7.23c.56-.672 1.495-.855 2.368-.8.096.007.193.01.291.01ZM5 16a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  cluster: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path d="M13 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM18 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM14 15a4 4 0 0 0-8 0v3h8v-3ZM6 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM16 15a4 4 0 0 0-4-4 4 4 0 0 0-4 4h8Z" />
    </svg>
  ),
  trial: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path
        fillRule="evenodd"
        d="M10 2c-2.236 0-4.43.18-6.57.524C1.993 2.755 1 4.014 1 5.426v5.148c0 1.413.993 2.67 2.43 2.902.848.137 1.705.248 2.57.331v3.443a.75.75 0 0 0 1.28.53l3.58-3.579a.78.78 0 0 1 .527-.224 41.202 41.202 0 0 0 5.183-.5c1.437-.232 2.43-1.49 2.43-2.903V5.426c0-1.413-.993-2.67-2.43-2.902A41.289 41.289 0 0 0 10 2Zm0 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM8 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm5 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  chevronDown: (
    <svg
      viewBox="0 0 16 16"
      fill="currentColor"
      className="h-3.5 w-3.5 shrink-0 transition-transform duration-200"
    >
      <path
        fillRule="evenodd"
        d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
      <path
        fillRule="evenodd"
        d="M3 4.25A2.25 2.25 0 0 1 5.25 2h5.5A2.25 2.25 0 0 1 13 4.25v2a.75.75 0 0 1-1.5 0v-2a.75.75 0 0 0-.75-.75h-5.5a.75.75 0 0 0-.75.75v11.5c0 .414.336.75.75.75h5.5a.75.75 0 0 0 .75-.75v-2a.75.75 0 0 1 1.5 0v2A2.25 2.25 0 0 1 10.75 18h-5.5A2.25 2.25 0 0 1 3 15.75V4.25Zm10.47 4.22a.75.75 0 0 1 1.06 0l2.25 2.25a.75.75 0 0 1 0 1.06l-2.25 2.25a.75.75 0 1 1-1.06-1.06l.97-.97H8.75a.75.75 0 0 1 0-1.5h5.69l-.97-.97a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  ),
} as const;

// ─── Nav structure ────────────────────────────────────────────────
interface NavItem {
  label: string;
  to: string;
  icon: ReactElement;
}

interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

// Fallback icon for any DB-driven label we don't have a dedicated icon for
const DEFAULT_ICON = Icons.trial;

// Maps a Home menu's label (as returned by the API) to its existing icon.
// Extend this map if new labels are introduced on the backend.
const HOME_ICON_MAP: Record<string, ReactElement> = {
  Home: Icons.home,
  "Club Calendar": Icons.calendar,
  "Club Members": Icons.users,
  "Club Suppliers": Icons.suppliers,
  "Club Announcements": Icons.announcements,
  "Club Policies": Icons.policies,
  "Team 1": Icons.team,
  "Club Shop": Icons.shop,
  Fixtures: Icons.fixtures,
  "My Orders": Icons.orders,
};

// Shared with the Settings Page Access list so both show the same icon per page.
export function getMenuIcon(label: string): ReactElement {
  return HOME_ICON_MAP[label] ?? DEFAULT_ICON;
}

// Converts a menu "type" (e.g. "Club Calendar", "My Orders!") into a URL-safe
// path segment: lowercased, spaces -> hyphens, special chars stripped.
export function slugifyMenuType(
  type: string,
  label?: string,
  link?: string,
): string {
  const slug = label
    ? label
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
    : type
        ?.trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

  if (type === "HOME") return `/`;
  // Always route content menus through the generic content route.
  if (type === "CONTENT_CUSTOM") return `/content/${slug}`;

  return `/${slug}`;
}

// The Admin section stays static, as before.
const ADMIN_SECTION_ITEMS: NavItem[] = [
    { label: "Settings", to: ROUTES.SETTINGS, icon: Icons.trial },
    {
      label: "Register Requests",
      to: ROUTES.REGISTER_REQUESTS,
      icon: Icons.trial,
    },
    { label: "Invited", to: ROUTES.INVITED, icon: Icons.trial },
    { label: "Squad Builder", to: ROUTES.SQUAD_BUILDER, icon: Icons.users },
    {
      label: "Request Management",
      to: ROUTES.REQUEST_MANAGEMENT,
      icon: Icons.cluster,
    },
    { label: "Spaces", to: ROUTES.SPACES, icon: Icons.trial },
    {
      label: "Cluster Management",
      to: ROUTES.CLUSTER_MANAGEMENT,
      icon: Icons.management,
    },
    { label: "Products & Sizes", to: ROUTES.PRODUCTS, icon: Icons.shop },
    { label: "Coupon", to: ROUTES.COUPONS, icon: Icons.trial },
    { label: "Transactions", to: ROUTES.TRANSACTIONS, icon: Icons.trial },
];

const LEAGUE_MANAGEMENT_ITEM: NavItem = {
  label: "League Management",
  to: ROUTES.LEAGUE_MANAGEMENT,
  icon: Icons.trial,
};

// ─── Collapsible nav section ──────────────────────────────────────
interface NavSectionProps {
  section: NavSection;
  defaultOpen?: boolean;
}

const SidebarSection = memo(function SidebarSection({
  section,
  defaultOpen = true,
}: NavSectionProps) {
  const { pathname } = useLocation();
  const { close } = useSidebar();
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  return (
    <div>
      {/* Section header button */}
      <button
        onClick={toggle}
        aria-expanded={isOpen}
        className={cn(
          "flex w-full items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-widest",
          "transition-colors duration-150",
          "text-[var(--sidebar-text-heading)] hover:text-[var(--sidebar-text)]",
        )}
      >
        <span>{section.label}</span>
        <span
          className={cn(
            "transition-transform duration-200",
            isOpen ? "rotate-180" : "rotate-0",
          )}
        >
          {Icons.chevronDown}
        </span>
      </button>

      {/* Items — CSS max-height transition for smooth open/close */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          isOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <ul className="mt-0.5 space-y-0.5 pb-2">
          {section.items.map((item, idx) => {
            const isActive = pathname === item.to;
            return (
              <li key={`${item.to}-${idx}`}>
                <NavLink
                  to={item.to}
                  end
                  onClick={close}
                  className={cn(
                    "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium",
                    "transition-colors duration-150",
                    isActive
                      ? "bg-[var(--sidebar-bg-active)] text-[var(--sidebar-text-active)] shadow-sm"
                      : "text-[var(--sidebar-text)] hover:bg-[var(--sidebar-bg-hover)] hover:text-white",
                  )}
                >
                  <span
                    className={cn(
                      "transition-colors duration-150",
                      isActive
                        ? "text-[var(--sidebar-text-active)]"
                        : "text-[var(--sidebar-text)] group-hover:text-white",
                    )}
                  >
                    {item.icon}
                  </span>
                  {item.label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
});

// ─── Main Sidebar ─────────────────────────────────────────────────
const Sidebar = memo(function Sidebar() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { close } = useSidebar();
  const user = useAppSelector((s) => s.user.currentUser);
  const { role } = useAuth();
  const { currentEvent } = useEventsFeature();

  // Adjust this to your real admin check (role string, permission flag, etc).
  const { isAdmin } = useAuth();

  const homeSection: NavSection = useMemo(() => {
    const menus = currentEvent?.menus ?? [];

    const items: NavItem[] = menus
      .filter((menu: any) => {
        if (menu.adminOnly) return isAdmin;
        return menu.show === true;
      })
      .map((menu: any) => ({
        label: menu.label,
        to: slugifyMenuType(menu?.type, menu?.label, menu?.link),
        icon: getMenuIcon(menu.label),
      }));

    return { id: "home", label: "Home", items };
  }, [currentEvent, isAdmin]);

  // console.info("Sidebar: homeSection", homeSection, currentEvent?.menus);

  // Only include the Admin section for admin users
  const isLeagueEvent = String(currentEvent?.platformEventType ?? "").includes("LEAGUE");
  const adminSection: NavSection = useMemo(
    () => ({
      id: "admin",
      label: "Admin",
      items: isLeagueEvent
        ? [LEAGUE_MANAGEMENT_ITEM, ...ADMIN_SECTION_ITEMS]
        : ADMIN_SECTION_ITEMS,
    }),
    [isLeagueEvent],
  );

  const navSections: NavSection[] = useMemo(
    () => (isAdmin ? [homeSection, adminSection] : [homeSection]),
    [homeSection, adminSection, isAdmin],
  );

  const handleLogout = useCallback(() => {
    removeSessionCookies();
    dispatch(logout());
    void apolloClient.clearStore();
    void navigate(ROUTES.LOGIN);
  }, [dispatch, navigate]);

  const initials = user
    ? `${user.firstName[0] ?? ""}${user?.lastName?.[0] ?? ""}`.toUpperCase()
    : "?";

  // const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  return (
    <aside
      aria-label="Main navigation"
      style={{ backgroundColor: "var(--sidebar-bg)" }}
      className="flex h-full w-full flex-col"
    >
      {/* ── Brand logo area ── */}
      <div
        className="flex h-[var(--header-height)] shrink-0 items-center gap-3 px-4"
      >
        {/* Football icon as brand mark */}
        <div className="theme mx-auto flex h-max w-max shrink-0 items-center justify-center rounded-md p-1 text-white">
          <img
            src={
              currentEvent?.theme?.logoURL !== undefined &&
              currentEvent?.theme?.logoURL !== "undefined" &&
              currentEvent?.theme?.logoURL !== "" &&
              currentEvent?.theme?.logoURL !== null
                ? currentEvent?.theme?.logoURL
                : isClubleaDomain()
                  ? clublea_logo
                  : synkd_logo
            }
            alt=""
            className="min-w-20 max-w-20"
          />
        </div>

        {/* Close button — mobile only */}
        <button
          onClick={close}
          aria-label="Close navigation"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>
      </div>
      <div
        className="mx-3 h-px shrink-0"
        style={{ backgroundColor: "var(--sidebar-border)" }}
      />

      {/* ── Navigation ── */}
      <nav className="scrollbar-thin flex-1 overflow-y-auto px-3 py-3">
        <div className="space-y-1">
          {navSections.map((section, idx) => (
            <div key={section.id}>
              {idx > 0 && (
                <div
                  className="my-2 h-px w-full"
                  style={{ backgroundColor: "var(--sidebar-border)" }}
                />
              )}
              <SidebarSection section={section} defaultOpen={idx === 0} />
            </div>
          ))}
        </div>
      </nav>

      {/* ── User footer ── */}
      <div
        className="shrink-0 p-3"
        style={{ borderTop: "1px solid var(--sidebar-border)" }}
      >
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          {/* Avatar */}
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-bold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">
              {user ? `${user.firstName} ${user.lastName}` : "Guest"}
            </p>
            <p
              className="truncate text-xs"
              style={{ color: "var(--sidebar-text-heading)" }}
            >
              {role ?? "viewer"}
            </p>
          </div>
          <button
            onClick={handleLogout}
            aria-label="Log out"
            title="Log out"
            className="shrink-0 rounded-lg p-1.5 transition-colors duration-150 hover:bg-white/10"
            style={{ color: "var(--sidebar-text)" }}
          >
            {Icons.logout}
          </button>
        </div>
      </div>
    </aside>
  );
});

export default Sidebar;
